import axios from 'axios';
import config from '@config';
import {
  decodeToken,
  getToken,
  replaceToken,
  removeToken,
  isJWTExpired,
  responseFormatter,
  onTokenUpdate,
  getRCRedirectUri,
} from '@utils';
import { validateJwks } from './rc-api';
import { sessionExpireMsg } from '@shared/constants';

const noop = () => {
  return;
};
let refreshPromise = null;
let sessionExpired = false;
let isFetchingJWKS = null;
let startTime = Date.now();
let remaining = 100;
let requestWindow = 60;
let limit = 60;

const updateStartTime = async (rateLimitHook = noop) => {
  let delay = 0;
  let now = Date.now();

  let diff = now - startTime;
  if (remaining < 2) {
    delay = requestWindow * 1000 - diff;
    window.isRateLimited = true;
    window.rateLimitDelay = delay;
    rateLimitHook({
      delay,
      remaining,
      requestWindow,
      startTime,
      limit,
    });
    setTimeout(() => {
      window.isRateLimited = false;
      window.rateLimitDelay = 0;
    }, delay);
  } else {
    window.isRateLimited = false;
    window.rateLimitDelay = 0;
  }

  await new Promise(resolve => setTimeout(resolve, delay));
  return now + delay;
};

const apiInstance = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

window.isRateLimited = false;
window.rateLimitDelay = 0;

// When the refresh token is permanently invalid, clear the session and redirect
// to login exactly once instead of retrying the dead token into a rate limit.
const handleSessionExpired = () => {
  if (sessionExpired) return;
  sessionExpired = true;
  removeToken();
  window.location.replace(
    getRCRedirectUri(
      config.rc_authorize_uri,
      config.rc_client_Id,
      config.apiBaseUrl,
      window.location.href
    )
  );
};

// Single-flight refresh shared by all axios instances so the rotating
// RingCentral refresh token is only consumed once.
const getRefreshedToken = token => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken(token)
      .then(({ data, status, error }) => {
        if (data && data.accessToken) {
          replaceToken(data.accessToken);
          return data.accessToken;
        }

        // Only an authentication rejection (401, excluding "under maintenance")
        // should discard the session. Transient failures (network, timeout,
        // 429, 5xx) reject without clearing a potentially valid token.
        const errorText = Array.isArray(error)
          ? error[0] && error[0].message
          : error;
        const isUnderMaintenance =
          typeof errorText === 'string' &&
          errorText.toLowerCase() === 'under maintenance';
        if (status === 401 && !isUnderMaintenance) {
          handleSessionExpired();
        }

        throw { message: sessionExpireMsg, status, error };
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

apiInstance.interceptors.request.use(config => {
  if (config.setAuthHeader) {
    const token = getToken();
    config.headers.common['Authorization'] = token;
  }
  if (config.setTimeout) {
    config.timeout = config.setTimeout;
  }

  return config;
});

// Add a request interceptor
apiInstance.interceptors.request.use(
  async config => {
    // Do something before request is sent
    const originalRequest = config;
    let token = getToken();
    if (
      isJWTExpired(token) &&
      !/login/i.test(originalRequest.url) &&
      !/refreshtoken/i.test(originalRequest.url) &&
      !/profile-image/i.test(originalRequest.url) &&
      process.env?.NODE_ENV !== 'test'
    ) {
      if (sessionExpired) {
        return Promise.reject({ message: sessionExpireMsg });
      }
      const newToken = await getRefreshedToken(token);
      originalRequest.headers['Authorization'] = newToken;
      return originalRequest;
    } else {
      return config;
    }
  },
  error => {
    // Do something with request error
    return Promise.reject('Session expired. Redirecting to login screen...');
  }
);

const refreshAccessToken = async token => {
  return await responseFormatter(
    apiInstance.get('refreshtoken', { headers: { Authorization: token } })
  );
};

export default apiInstance;

export const rcApi = axios.create({
  baseURL: config.rcApiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

rcApi.interceptors.request.use(async config => {
  startTime = await updateStartTime();
  return config;
});

rcApi.interceptors.response.use(async response => {
  if (response.headers['x-rate-limit-remaining']) {
    remaining = response.headers['x-rate-limit-remaining'];
    console.log('RESPONSE', response.headers['x-rate-limit-remaining']);
  }
  if (response.headers['x-rate-limit-window']) {
    requestWindow = response.headers['x-rate-limit-window'];
  }
  if (response.headers['x-rate-limit-limit']) {
    limit = response.headers['x-rate-limit-limit'];
    console.log('LIMIT', response.headers['x-rate-limit-limit']);
  }
  return response;
});

rcApi.interceptors.request.use(config => {
  if (config.setAuthHeader) {
    const token = getToken();
    config.headers.common['Authorization'] = token;
  }

  return config;
});

rcApi.interceptors.request.use(
  async config => {
    // Do something before request is sent
    const originalRequest = config;
    let token = getToken();
    if (isJWTExpired(token)) {
      if (process.env?.NODE_ENV !== 'test') {
        if (sessionExpired) {
          return Promise.reject({ message: sessionExpireMsg });
        }
        const newToken = await getRefreshedToken(token);
        const decodedToken = decodeToken(newToken);
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${decodedToken.rc_access_token}`;

        if (originalRequest.headers.access_token) {
          originalRequest.headers.access_token = decodedToken.rc_access_token;
        }
      }

      return originalRequest;
    } else {
      if (!config.hasToken) {
        const decodedToken = decodeToken(getToken());
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${decodedToken.rc_access_token}`;
      }
      return config;
    }
  },
  error => {
    // Redirect user to login screen.
    return Promise.reject('Session expired. Redirecting to login screen...');
  }
);

export const jwksApi = axios.create({
  baseURL: config.rcApiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

jwksApi.interceptors.request.use(async config => {
  startTime = await updateStartTime(config.hooks?.rateLimitReached || noop);
  return config;
});

jwksApi.interceptors.response.use(async response => {
  if (response.headers['x-rate-limit-remaining']) {
    remaining = response.headers['x-rate-limit-remaining'];
    console.log('RESPONSE', response.headers['x-rate-limit-remaining']);
  }
  if (response.headers['x-rate-limit-window']) {
    requestWindow = response.headers['x-rate-limit-window'];
  }
  if (response.headers['x-rate-limit-limit']) {
    limit = response.headers['x-rate-limit-limit'];
    console.log('LIMIT', response.headers['x-rate-limit-limit']);
  }
  return response;
});

jwksApi.interceptors.request.use(async config => {
  const { hasToken, accountUid } = config;
  if (hasToken) {
    const isValidToken = await validateJwks(hasToken, accountUid);
    if (isValidToken.error) {
      onTokenUpdate();
      return Promise.reject(isValidToken.error);
    }

    config.headers['Authorization'] = `Bearer ${
      isValidToken.token && isValidToken.token.accessToken
    }`;

    if (isValidToken.hasUpdatedToken) {
      config.hasUpdatedToken = true;
      config.updatedToken = isValidToken.token;
    }

    return config;
  } else {
    const decodedToken = decodeToken(getToken());
    config.headers['Authorization'] = `Bearer ${decodedToken.rc_access_token}`;
  }
  return config;
});

jwksApi.interceptors.response.use(
  async response => {
    if (response.config.hasUpdatedToken) {
      response.data = {
        ...response.data,
        token: response.config.updatedToken,
      };
    }
    return response;
  },
  error => {
    if (error.config && error.config.hasToken) {
      error.origin = 'JWKS';
      if (error.response && error.response.status === 401) {
        const getNewToken =
          isFetchingJWKS || onTokenUpdate(null, error.config.accountUid);
        isFetchingJWKS = getNewToken;
        return (
          getNewToken &&
          getNewToken
            .then(token => {
              if (token) {
                error.config.headers.Authorization = `Bearer ${token.accessToken}`;
                error.config.hasToken = token;
                setTimeout(() => (isFetchingJWKS = null), 700);
                return jwksApi.request(error.config);
              }
            })
            .catch(tokenError => {
              Promise.reject(tokenError);
            })
        );
      }
    }
    return Promise.reject(error);
  }
);
